/**
 * 158 (release 1.40) — a product's codes: its primary `barcode` (labels,
 * receipts, the shared catalog, the shop's own 29… numbering) plus up to ten
 * `extra_barcodes` that only FIND it. Lilu's FINA cells read
 * „4820151980077 / 4820188690284": one bag, two codes.
 *
 * Every lookup in the app comes here, so a code means the same thing on the
 * till, the kiosk, the stocktake and a waybill. A device copy saved by 1.39
 * has no `extra_barcodes` at all; every reader below treats that as none.
 */

import { isCardText } from './club'

export const MAX_EXTRA_CODES = 10
export const MAX_CODE_LEN = 64

export type Coded = { barcode?: string | null; extra_barcodes?: readonly string[] | null }

/** The extras, trimmed, once; a missing or broken value is none. */
export function extrasOf(p: Coded): string[] {
  const raw = p.extra_barcodes
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const c of raw) {
    const t = typeof c === 'string' ? c.trim() : ''
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** Every code of a product: the primary first. */
export function productCodes(p: Coded): string[] {
  const primary = (p.barcode ?? '').trim()
  const out = primary ? [primary] : []
  for (const c of extrasOf(p)) if (!out.includes(c)) out.push(c)
  return out
}

/**
 * A code that survives our own Excel round trip: the importer splits a cell on
 * `/ , ;` and line breaks and reads a „PLU 24"-shaped part as a scale PLU
 * (lib/import-codes.ts, the same pattern), so such a code would come back as
 * two codes or as a PLU. „PLUSH-01" is a code to it, and stays one. None
 * existed on 2026-10-13 (checked live); the screens refuse them so none ever
 * does (158, the review's ruling). Trimmed, non-empty, ≤ 64. Never the club
 * card form (`9917` + 36 digits): the till reads that as a card first, so a
 * product carrying it could never be scanned (club A).
 */
export function isStorableCode(code: string): boolean {
  const c = code.trim()
  return c !== '' && c.length <= MAX_CODE_LEN && !/[/,;\r\n]/.test(c) && !/^plu\s*:?\s*\d+$/i.test(c) && !isCardText(c)
}

/**
 * The database's own rule (158's BEFORE trigger), applied before a save so
 * the trigger never surprises: trimmed, no empties, no repeats, never the
 * primary; with no primary the first extra becomes it. `tooMany` / `tooLong`
 * are for the screen to refuse — the database would.
 */
export function normalizeCodes(
  primary: string | null | undefined,
  extras: readonly string[],
): { barcode: string | null; extras: string[]; tooMany: boolean; tooLong: string | null } {
  let barcode = (primary ?? '').trim() || null
  let out: string[] = []
  let tooLong: string | null = null
  for (const raw of extras) {
    const c = (raw ?? '').trim()
    if (!c) continue
    if (c.length > MAX_CODE_LEN && tooLong === null) tooLong = c
    if (c === barcode || out.includes(c)) continue
    out.push(c)
  }
  if (barcode === null && out.length > 0) { barcode = out[0]; out = out.slice(1) }
  return { barcode, extras: out, tooMany: out.length > MAX_EXTRA_CODES, tooLong }
}

/**
 * What a product card's save checks and sends of its codes (the 1.40 final
 * review, I1 + M4).
 * - The primary is held to isStorableCode only when it was typed in this save
 *   — a new product, or an edit that changed it. An RS.GE waybill can make a
 *   product with a supplier's raw „A/12"; a price edit must not be refused for
 *   it. A restock found by a code never changes the primary: never checked.
 * - The extras go only with a new product or when the chips were touched (the
 *   153 `online_touched` pattern): the card holds the list it loaded, and
 *   sending it with a price edit would erase a code another device attached
 *   meanwhile. Left out, the PATCH leaves the column alone.
 */
export function cardCodeSave(o: {
  mode: 'new' | 'edit' | 'restock'
  typed: string
  loaded: string | null | undefined
  touched: boolean
}): { checkPrimary: boolean; sendExtras: boolean } {
  const checkPrimary = o.mode === 'new' || (o.mode === 'edit' && o.typed.trim() !== (o.loaded ?? '').trim())
  return { checkPrimary, sendExtras: o.mode === 'new' || o.touched }
}

// The store replaces its products array on every change and never mutates it
// (checked by Claude and Codex, 2026-10-13), so an index per array is never
// stale. A WeakMap lets old arrays go.
const indexes = new WeakMap<object, Map<string, unknown>>()

/** code → product for a whole list; the first product wins a code twice-held. */
export function codeIndex<T extends Coded>(products: readonly T[]): Map<string, T> {
  let m = indexes.get(products) as Map<string, T> | undefined
  if (!m) {
    m = new Map<string, T>()
    for (const p of products) for (const c of productCodes(p)) if (!m.has(c)) m.set(c, p)
    indexes.set(products, m)
  }
  return m
}

export function findByCode<T extends Coded>(products: readonly T[], code: string | null | undefined): T | undefined {
  const k = (code ?? '').trim()
  return k ? codeIndex(products).get(k) : undefined
}

export function hasCode(products: readonly Coded[], code: string | null | undefined): boolean {
  return findByCode(products, code) !== undefined
}

/**
 * 180 (1.58) — a box's own code. Only the till reads it: it adds the whole box
 * (`pack_qty` pieces), and it is tried BEFORE a scale label (`isPackCode`).
 * `findByCode` never returns a product for its box code, so receive, stocktake,
 * labels, the price check, the kiosk and the exchange say „not found" — never
 * one piece for a box (Codex's design review #3). A box code with no box size
 * is no box, and neither is the box of a product no longer sold by the piece —
 * made kg, a service or serial after it had one (Codex's final review I2; 181
 * refuses that pair in the database too).
 */
export type PackCoded = Coded & {
  pack_barcode?: string | null; pack_qty?: number | null
  unit?: string | null; is_service?: boolean | null; track_serial?: boolean | null
}

const soldByThePiece = (p: PackCoded) => (!p.unit || p.unit === 'piece') && !p.is_service && !p.track_serial

const packIndexes = new WeakMap<readonly PackCoded[], Map<string, PackCoded>>()

function packIndex<T extends PackCoded>(products: readonly T[]): Map<string, T> {
  let m = packIndexes.get(products) as Map<string, T> | undefined
  if (!m) {
    m = new Map<string, T>()
    for (const p of products) {
      const c = (p.pack_barcode ?? '').trim()
      if (c && (p.pack_qty ?? 0) >= 2 && soldByThePiece(p) && !m.has(c)) m.set(c, p)
    }
    packIndexes.set(products, m)
  }
  return m
}

export function isPackCode(products: readonly PackCoded[], code: string | null | undefined): boolean {
  const k = (code ?? '').trim()
  return k !== '' && packIndex(products).has(k)
}

/** The till's scan: a piece code adds one, a box code adds the box. */
export function findForTill<T extends PackCoded>(products: readonly T[], code: string | null | undefined): { product: T; units: number } | undefined {
  const k = (code ?? '').trim()
  if (!k) return undefined
  const piece = findByCode(products, k)
  if (piece) return { product: piece, units: 1 }
  const box = packIndex(products).get(k)
  return box ? { product: box, units: box.pack_qty as number } : undefined
}

/** Every code the shop has — store-code numbering, the catalog's „already stocked". */
export function allCodes(products: readonly Coded[]): Set<string> {
  return new Set(codeIndex(products).keys())
}

/** A search box: any of the product's codes contains the query (case-blind). */
export function codeMatches(p: Coded, q: string): boolean {
  const needle = q.trim().toLowerCase()
  if (!needle) return false
  return productCodes(p).some((c) => c.toLowerCase().includes(needle))
}

/** The product that already has this code — never the one being edited. */
export function codeOwner<T extends Coded & { id: string }>(
  products: readonly T[], code: string, exceptId?: string | null,
): T | undefined {
  const owner = findByCode(products, code)
  return owner && owner.id !== exceptId ? owner : undefined
}

export type AttachScan<T> = { kind: 'select'; product: T } | { kind: 'same' } | { kind: 'unknown' }

/**
 * What a gun burst may do inside the till's „attach this code" dialog
 * (Codex #4, the 1.38 lesson: a scanner's Enter pressed the wrong button).
 * It may SELECT the product whose old code was scanned — the quickest way —
 * and nothing else: there is no answer here that confirms.
 */
export function attachScan<T extends Coded>(products: readonly T[], scanned: string, pending: string): AttachScan<T> {
  const k = scanned.trim()
  if (k && k === pending.trim()) return { kind: 'same' }
  const product = findByCode(products, k)
  return product ? { kind: 'select', product } : { kind: 'unknown' }
}
